import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { SIMA_TROPHY, type StratumId } from '@somnhold/content';
import { emis, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The Chasm's trophy (docs/SIMA.md, «Lo que se ve en el bastión»): Hilde's clan's winch beside the forge. A round shaft
 * walled in rough stone, its inner lip lit dragon red from below; over it two braced A-frames of dark timber, a crossbeam
 * with an iron pulley, the windlass behind with its cranks, and the rope dropping into the dark. On the rope, one thing
 * per stratum whose guardian the group felled, tied lower the deeper that threshold was. Beside it a standing slab with
 * the group's mark carved and burning red.
 *
 * It grows (`SIMA_TROPHY`): a second pulley with a hanging iron kibble from the tenth floor; Hilde's clan's banners from
 * the twenty-fifth; from the fortieth the rim is scorched (the smoke and the pulse are the view's: engine/world.ts).
 *
 * Draw calls: every piece is plain paint with one finish (flat shaded) or the one red self-lit material, so baked
 * (bakeStill) it is two draws; the glows go to the view's glow layer. No translucency (the frost jar is opaque glass).
 */

export interface WinchLook {
  /** The group's mark ever (the deepest floor finished): carved on the slab, and what makes it grow. */
  mark: number;
  /** A stone on the rope for each stratum whose guardian fell, at the floor it first fell on. */
  stones: { stratum: StratumId; floor: number }[];
}

/** Where the shaft's heart is, in the model's frame (for a flare). */
export const WINCH_SPOT = V(0, 0.6, 0);
/** Where the slab's mark is, in the model's frame. */
export const WINCH_SLAB_SPOT = V(1.32, 0.78, 0.86);

const RED = '#ff4f3a';
/** The deepest floor the rope reaches down to (a stone for a floor past it hangs at the bottom). */
const ROPE_FLOORS = 60;
const ROPE_TOP = 2.08;
const ROPE_BOTTOM = 0.78;

/** Seven-segment digits (a b c d e f g), as cut by a dwarf with a chisel. */
const SEGS: Record<string, string> = { '0': 'abcdef', '1': 'bc', '2': 'abged', '3': 'abgcd', '4': 'fgbc', '5': 'afgcd', '6': 'afgedc', '7': 'abc', '8': 'abcdefg', '9': 'abcdfg' };

type Put = (key: string, geo: THREE.BufferGeometry) => void;

/** Each stratum's token, built round (0, 0, 0) where its cord ties on, hanging down from there. */
const token = (stratum: StratumId, put: Put, glow: (color: string, size: number, y: number) => void): void => {
  switch (stratum) {
    case 'ossuary':
      // A flake of grey slate, its layers showing.
      put('slate', new THREE.BoxGeometry(0.26, 0.2, 0.05).rotateZ(0.2).translate(0, -0.13, 0));
      put('slateDark', new THREE.BoxGeometry(0.2, 0.015, 0.055).rotateZ(0.2).translate(0.01, -0.1, 0));
      put('slateDark', new THREE.BoxGeometry(0.18, 0.015, 0.055).rotateZ(0.2).translate(-0.01, -0.16, 0));
      break;
    case 'marrow':
      // A lump of basalt split open, an ember alive inside.
      put('basalt', new THREE.DodecahedronGeometry(0.13, 0).scale(1, 0.85, 0.9).translate(0, -0.13, 0));
      put('red', new THREE.OctahedronGeometry(0.06, 0).translate(0.02, -0.12, 0.09));
      glow('#ff7a3a', 0.45, -0.12);
      break;
    case 'glass':
      // A shard of black obsidian, sharp and glassy.
      put('obsidian', new THREE.OctahedronGeometry(0.11, 0).scale(0.8, 1.7, 0.55).rotateZ(0.15).translate(0, -0.18, 0));
      break;
    case 'cisterns':
      // A dark-teal stone from the cisterns, still dripping.
      put('teal', new THREE.DodecahedronGeometry(0.12, 0).scale(1, 0.9, 1).translate(0, -0.13, 0));
      put('water', new THREE.ConeGeometry(0.025, 0.07, 5).rotateX(Math.PI).translate(0.03, -0.27, 0.02));
      put('water', new THREE.SphereGeometry(0.025, 5, 4).translate(0.03, -0.33, 0.02));
      break;
    case 'buried':
      // A gnarled piece of root, two rootlets off it.
      put('root', new THREE.CylinderGeometry(0.035, 0.05, 0.3, 6).rotateZ(0.35).translate(0, -0.15, 0));
      put('root', new THREE.CylinderGeometry(0.015, 0.025, 0.16, 5).rotateZ(-0.9).translate(0.07, -0.2, 0.01));
      put('root', new THREE.CylinderGeometry(0.012, 0.02, 0.12, 5).rotateZ(1.1).translate(-0.08, -0.26, 0));
      put('rootDark', new THREE.SphereGeometry(0.05, 5, 4).translate(0.04, -0.03, 0));
      break;
    case 'grotto':
      // A cap of the grotto's mushroom, glowing cyan.
      put('cap', new THREE.SphereGeometry(0.12, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.7, 1).translate(0, -0.13, 0));
      put('stem', new THREE.CylinderGeometry(0.03, 0.04, 0.13, 6).translate(0, -0.19, 0));
      put('capDots', new THREE.SphereGeometry(0.022, 4, 3).translate(0.05, -0.08, 0.06));
      put('capDots', new THREE.SphereGeometry(0.018, 4, 3).translate(-0.06, -0.09, 0.04));
      glow('#5ff2ff', 0.55, -0.14);
      break;
    case 'frost':
      // Frost from the frozen stratum, kept in a stoppered jar.
      put('jar', new THREE.CylinderGeometry(0.08, 0.085, 0.2, 8).translate(0, -0.15, 0));
      put('frost', new THREE.CylinderGeometry(0.075, 0.08, 0.1, 8).translate(0, -0.2, 0.003));
      put('cork', new THREE.CylinderGeometry(0.05, 0.045, 0.05, 6).translate(0, -0.03, 0));
      glow('#cfeaff', 0.45, -0.17);
      break;
    case 'geode':
      // A cluster of violet crystal.
      put('geodeRock', new THREE.DodecahedronGeometry(0.08, 0).translate(0, -0.08, 0));
      put('violet', new THREE.OctahedronGeometry(0.06, 0).scale(0.7, 1.9, 0.7).translate(0, -0.2, 0.02));
      put('violet', new THREE.OctahedronGeometry(0.05, 0).scale(0.7, 1.7, 0.7).rotateZ(0.5).translate(0.06, -0.17, 0.03));
      put('violet', new THREE.OctahedronGeometry(0.045, 0).scale(0.7, 1.6, 0.7).rotateZ(-0.6).translate(-0.06, -0.16, 0.02));
      glow('#b07aff', 0.5, -0.17);
      break;
    case 'archive':
      // A scroll from the archive, sealed with red wax.
      put('parchment', new THREE.CylinderGeometry(0.045, 0.045, 0.26, 8).rotateZ(Math.PI / 2).translate(0, -0.08, 0));
      put('parchDark', new THREE.CylinderGeometry(0.048, 0.048, 0.02, 8).rotateZ(Math.PI / 2).translate(0.06, -0.08, 0));
      put('wax', new THREE.CylinderGeometry(0.035, 0.035, 0.02, 8).rotateX(Math.PI / 2).translate(0, -0.08, 0.05));
      put('wax', new THREE.ConeGeometry(0.015, 0.06, 4).rotateZ(0.3).translate(0.012, -0.12, 0.045));
      break;
    case 'forge':
      // A soot-black iron ring from the drowned forge.
      put('sootIron', new THREE.TorusGeometry(0.1, 0.03, 5, 10).translate(0, -0.12, 0));
      break;
    case 'nest':
      // A silk cocoon from the nest, wound tight.
      put('silk', new THREE.SphereGeometry(0.08, 7, 5).scale(1, 1.8, 1).translate(0, -0.16, 0));
      for (let i = 0; i < 4; i++) put('silkDark', new THREE.TorusGeometry(0.07 - Math.abs(i - 1.5) * 0.012, 0.008, 3, 10).rotateX(Math.PI / 2 + (i - 1.5) * 0.25).translate(0, -0.1 - i * 0.04, 0));
      break;
    case 'ribs':
      // A shard of grey bone off the titan's ribs.
      put('bone', new THREE.ConeGeometry(0.05, 0.3, 5).rotateX(Math.PI).rotateZ(0.25).translate(0.02, -0.17, 0));
      put('bone', new THREE.SphereGeometry(0.055, 5, 4).scale(1.2, 0.8, 1).translate(0, -0.03, 0));
      break;
  }
};

/** How far down the rope a threshold's stone hangs (deeper floors, lower), in the model's frame. */
export const stoneY = (floor: number): number => ROPE_TOP - (Math.min(ROPE_FLOORS, Math.max(5, floor)) / ROPE_FLOORS) * (ROPE_TOP - ROPE_BOTTOM);

export const simaWinchTrophy = (o?: BaseOpts & WinchLook): Model => {
  const c = begin(o, 1.3, 233);
  const { g } = c;
  const mark = Math.max(0, o?.mark ?? 0);
  const stones = (o?.stones ?? []).slice(0, 12);
  const pulley2 = mark >= SIMA_TROPHY.pulley;
  const banners = mark >= SIMA_TROPHY.banners;
  const smoke = mark >= SIMA_TROPHY.smoke;
  const parts: Record<string, THREE.BufferGeometry[]> = {};
  const put: Put = (key, geo) => {
    (parts[key] ??= []).push(geo);
  };
  const glows: [string, number, THREE.Vector3, number][] = [];
  // The shaft: a ring of rough-cut blocks round a black mouth.
  const R = 0.82;
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const h = 0.26 + ((i * 7) % 3) * 0.03;
    put(i % 3 === 0 ? 'rimDark' : 'rim', new THREE.BoxGeometry(0.36, h, 0.24).rotateY(-a + Math.PI / 2).translate(Math.cos(a) * R, h / 2, Math.sin(a) * R));
    // Scorched from the fortieth floor: soot down the blocks' outer faces.
    if (smoke && i % 2 === 0) put('soot', new THREE.BoxGeometry(0.28, h * 0.7, 0.02).rotateY(-a + Math.PI / 2).translate(Math.cos(a) * (R + 0.125), h * 0.5, Math.sin(a) * (R + 0.125)));
  }
  // Copestones on top, a flat lip to lean on.
  for (let i = 0; i < 8; i++) {
    const a = ((i + 0.5) / 8) * Math.PI * 2;
    put('cope', new THREE.BoxGeometry(0.6, 0.06, 0.3).rotateY(-a + Math.PI / 2).translate(Math.cos(a) * R, 0.33, Math.sin(a) * R));
  }
  put('void', new THREE.CylinderGeometry(R - 0.1, R - 0.1, 0.04, 20).translate(0, 0.03, 0));
  // The light from below: a lit band on the inner lip and a deeper ring down in the dark.
  put('red', new THREE.TorusGeometry(R - 0.12, 0.025, 4, 24).rotateX(Math.PI / 2).translate(0, 0.3, 0));
  put('red', new THREE.TorusGeometry(R - 0.32, 0.05, 4, 20).rotateX(Math.PI / 2).translate(0, 0.06, 0));
  put('red', new THREE.CircleGeometry(0.22, 12).rotateX(-Math.PI / 2).translate(0, 0.055, 0));
  glows.push([RED, 2.1, V(0, 0.35, 0), smoke ? 0.5 : 0.4]);
  glows.push(['#ff8a6e', 1.0, V(0, 0.15, 0), 0.5]);
  // The frames: an A of two legs each side, braced, standing outside the rim.
  const top = 2.5;
  for (const sx of [-1, 1]) {
    const x = sx * 1.12;
    for (const sz of [-1, 1]) {
      const leg = new THREE.BoxGeometry(0.12, 2.62, 0.12);
      leg.rotateX(sz * 0.27).translate(x, 1.26, sz * 0.34);
      put('wood', leg);
      put('iron', new THREE.BoxGeometry(0.18, 0.06, 0.18).translate(x, 0.03, sz * 0.68));
    }
    put('plank', new THREE.BoxGeometry(0.08, 0.08, 1.05).translate(x, 0.75, 0));
    put('iron', new THREE.BoxGeometry(0.16, 0.18, 0.16).translate(x, top, 0));
    for (const y of [top - 0.02, top + 0.06]) put('rivet', new THREE.SphereGeometry(0.02, 4, 3).translate(x + sx * 0.08, y, 0));
  }
  // The crossbeam over the shaft, banded with iron.
  put('wood', new THREE.BoxGeometry(2.55, 0.15, 0.17).translate(0, top + 0.09, 0));
  for (const x of [-0.7, 0.7]) put('iron', new THREE.BoxGeometry(0.06, 0.18, 0.2).translate(x, top + 0.09, 0));
  // The pulley: an iron sheave in a fork under the beam's middle.
  const sheave = (x: number, r: number): void => {
    put('iron', new THREE.TorusGeometry(r, 0.035, 5, 14).translate(x, top - 0.2, 0));
    put('ironDark', new THREE.CylinderGeometry(r - 0.02, r - 0.02, 0.04, 12).rotateX(Math.PI / 2).translate(x, top - 0.2, 0));
    put('rivet', new THREE.CylinderGeometry(0.035, 0.035, 0.14, 6).rotateX(Math.PI / 2).translate(x, top - 0.2, 0));
    for (const sz of [-1, 1]) put('iron', new THREE.BoxGeometry(0.05, 0.26, 0.03).translate(x, top - 0.09, sz * 0.065));
  };
  sheave(0, 0.16);
  // The windlass behind the shaft: a drum between the frames' back legs, rope wound on it, a crank at each end.
  const dz = -0.62;
  const dy = 0.95;
  put('wood', new THREE.CylinderGeometry(0.12, 0.12, 2.1, 8).rotateZ(Math.PI / 2).translate(0, dy, dz));
  for (let i = 0; i < 7; i++) put('rope', new THREE.TorusGeometry(0.135, 0.022, 4, 10).rotateY(Math.PI / 2).translate(-0.3 + i * 0.07, dy, dz));
  for (const sx of [-1, 1]) {
    put('iron', new THREE.CylinderGeometry(0.15, 0.15, 0.04, 8).rotateZ(Math.PI / 2).translate(sx * 0.62, dy, dz));
    put('iron', new THREE.BoxGeometry(0.05, 0.36, 0.05).translate(sx * 1.24, dy - 0.15 * sx, dz));
    put('grip', new THREE.CylinderGeometry(0.03, 0.03, 0.22, 6).rotateZ(Math.PI / 2).translate(sx * 1.32, dy - 0.3 * sx, dz));
  }
  // The rope: from the drum up over the pulley and straight down into the shaft.
  const run = (a: THREE.Vector3, b: THREE.Vector3, r: number): void => {
    const d = b.clone().sub(a);
    const geo = new THREE.CylinderGeometry(r, r, d.length(), 5);
    geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), d.clone().normalize()));
    const m = a.clone().add(b).multiplyScalar(0.5);
    put('rope', geo.translate(m.x, m.y, m.z));
  };
  run(V(0, dy + 0.13, dz), V(0, top - 0.2 + 0.16, -0.04), 0.025);
  run(V(0, top - 0.2, 0.15), V(0, 0.02, 0.15), 0.028);
  // A weight at the rope's foot, down in the light.
  put('iron', new THREE.CylinderGeometry(0.07, 0.09, 0.14, 6).translate(0, 0.16, 0.15));
  // The stones, each on a short cord off the rope, by the floor its guardian fell on; the sides alternate.
  stones.forEach((st, i) => {
    const y = stoneY(st.floor);
    // Round the rope in a slow spiral, so from the table above they spread out instead of hiding one another.
    const a = 0.6 + i * 2.2;
    const side = Math.cos(a) < 0 ? -1 : 1;
    const tie = V(0, y + 0.06, 0.15);
    const at = V(Math.cos(a) * 0.3, y - 0.04, 0.15 + Math.sin(a) * 0.26);
    run(tie, at, 0.008);
    put('rope', new THREE.TorusGeometry(0.035, 0.012, 3, 8).rotateX(Math.PI / 2).translate(0, y + 0.06, 0.15));
    const sub: Record<string, THREE.BufferGeometry[]> = {};
    token(
      st.stratum,
      (k, geo) => (sub[k] ??= []).push(geo),
      (color, size, gy) => glows.push([color, size, V(at.x, at.y + gy, at.z + 0.03), 0.6]),
    );
    for (const [k, list] of Object.entries(sub)) for (const geo of list) put(k, geo.scale(1.35, 1.35, 1.35).rotateY(side * 0.3).translate(at.x, at.y, at.z));
  });
  // From the tenth floor: a second sheave and a miner's kibble on its own line, hung by the shaft's side.
  if (pulley2) {
    const kx = -0.78;
    sheave(kx, 0.12);
    run(V(kx, top - 0.2, 0.13), V(kx, 1.72, 0.13), 0.02);
    put('iron', new THREE.CylinderGeometry(0.19, 0.14, 0.28, 8, 1, true).translate(kx, 1.48, 0.13));
    put('ironDark', new THREE.CylinderGeometry(0.14, 0.14, 0.02, 8).translate(kx, 1.35, 0.13));
    put('iron', new THREE.TorusGeometry(0.19, 0.02, 4, 12).rotateX(Math.PI / 2).translate(kx, 1.62, 0.13));
    put('iron', new THREE.TorusGeometry(0.19, 0.015, 3, 12, Math.PI).translate(kx, 1.62, 0.13));
    put('ore', new THREE.DodecahedronGeometry(0.08, 0).translate(kx - 0.04, 1.6, 0.12));
    put('ore', new THREE.DodecahedronGeometry(0.07, 0).translate(kx + 0.07, 1.59, 0.18));
  }
  // From the twenty-fifth: Hilde's clan's banners on poles behind the frames, charcoal with the red anvil.
  if (banners) {
    for (const sx of [-1, 1]) {
      const x = sx * 1.55;
      const z = -0.45;
      put('wood', new THREE.CylinderGeometry(0.035, 0.045, 3.1, 6).translate(x, 1.55, z));
      put('iron', new THREE.ConeGeometry(0.06, 0.16, 5).translate(x, 3.18, z));
      put('wood', new THREE.CylinderGeometry(0.02, 0.02, 0.62, 5).rotateZ(Math.PI / 2).translate(x - sx * 0.3, 2.9, z + 0.02));
      // The cloth hangs from the arm, toward the shaft, a swallowtail at the foot.
      const bx = x - sx * 0.3;
      put('banner', new THREE.BoxGeometry(0.56, 1.0, 0.02).translate(bx, 2.38, z + 0.03));
      put('banner', new THREE.BoxGeometry(0.2, 0.26, 0.02).rotateZ(0.25).translate(bx - 0.17, 1.8, z + 0.03));
      put('banner', new THREE.BoxGeometry(0.2, 0.26, 0.02).rotateZ(-0.25).translate(bx + 0.17, 1.8, z + 0.03));
      // The clan's anvil: horn, face, waist and foot, in red wool.
      put('wool', new THREE.BoxGeometry(0.32, 0.07, 0.01).translate(bx, 2.5, z + 0.045));
      put('wool', new THREE.ConeGeometry(0.035, 0.12, 4).rotateZ(Math.PI / 2).translate(bx - 0.21, 2.5, z + 0.045));
      put('wool', new THREE.BoxGeometry(0.1, 0.12, 0.01).translate(bx, 2.41, z + 0.045));
      put('wool', new THREE.BoxGeometry(0.24, 0.05, 0.01).translate(bx, 2.33, z + 0.045));
      put('wool', new THREE.BoxGeometry(0.46, 0.03, 0.01).translate(bx, 2.78, z + 0.045));
    }
  }
  // The slab beside the shaft, toward the camera: the group's mark cut in it, burning red.
  const slab = G(g, WINCH_SLAB_SPOT.x, 0, WINCH_SLAB_SPOT.z);
  slab.rotation.y = -0.45;
  const sp: Record<string, THREE.BufferGeometry[]> = {};
  const sput: Put = (k, geo) => (sp[k] ??= []).push(geo);
  sput('slab', new THREE.BoxGeometry(0.72, 1.12, 0.16).translate(0, 0.56, 0));
  sput('slabDark', new THREE.BoxGeometry(0.62, 0.08, 0.17).translate(0, 1.1, 0));
  sput('slabDark', new THREE.BoxGeometry(0.86, 0.12, 0.3).translate(0, 0.06, 0));
  // A pick and a rope coil cut above the number: the Chasm's sign.
  sput('red', new THREE.BoxGeometry(0.24, 0.025, 0.01).rotateZ(0.5).translate(0, 0.98, 0.083));
  sput('red', new THREE.BoxGeometry(0.24, 0.025, 0.01).rotateZ(-0.5).translate(0, 0.98, 0.083));
  if (mark > 0) {
    const txt = String(Math.min(mark, 999));
    const w = 0.15;
    const hgt = 0.3;
    const gap = 0.07;
    const span = txt.length * w + (txt.length - 1) * gap;
    const sc = Math.min(1, 0.56 / span);
    const t = 0.028;
    [...txt].forEach((ch, i) => {
      const cx = (-span / 2 + w / 2 + i * (w + gap)) * sc;
      const cy = 0.58;
      const seg = (name: string): THREE.BufferGeometry | null => {
        const hw = (w / 2) * sc;
        const hh = (hgt / 2) * sc;
        switch (name) {
          case 'a': return new THREE.BoxGeometry(w * sc, t, 0.01).translate(cx, cy + hh, 0.083);
          case 'd': return new THREE.BoxGeometry(w * sc, t, 0.01).translate(cx, cy - hh, 0.083);
          case 'g': return new THREE.BoxGeometry(w * sc, t, 0.01).translate(cx, cy, 0.083);
          case 'b': return new THREE.BoxGeometry(t, hh + t / 2, 0.01).translate(cx + hw, cy + hh / 2, 0.083);
          case 'c': return new THREE.BoxGeometry(t, hh + t / 2, 0.01).translate(cx + hw, cy - hh / 2, 0.083);
          case 'e': return new THREE.BoxGeometry(t, hh + t / 2, 0.01).translate(cx - hw, cy - hh / 2, 0.083);
          case 'f': return new THREE.BoxGeometry(t, hh + t / 2, 0.01).translate(cx - hw, cy + hh / 2, 0.083);
        }
        return null;
      };
      for (const s of SEGS[ch] ?? '') {
        const geo = seg(s);
        if (geo) sput('red', geo);
      }
    });
    // The cut is dark round the light, as if burnt into the stone.
    sput('slabDark', new THREE.BoxGeometry(Math.min(0.6, span * sc + 0.12), 0.42 * Math.max(sc, 0.75), 0.005).translate(0, 0.58, 0.081));
    glows.push(['#ff6a4a', 0.9, V(WINCH_SLAB_SPOT.x, 0.6, WINCH_SLAB_SPOT.z + 0.15), 0.42]);
  }
  const mats: Record<string, THREE.Material> = {
    rim: stone('#5d6068'),
    rimDark: stone('#474950'),
    cope: stone('#6c7078'),
    soot: stone('#141316'),
    void: std('#040304', { roughness: 1, flatShading: true }),
    red: emis(RED, RED, 1.6, { flatShading: true }),
    wood: std('#5e4636', { roughness: 0.9, flatShading: true }),
    plank: std('#4a3729', { roughness: 0.9, flatShading: true }),
    iron: metal('#4b4f57', { roughness: 0.5, flatShading: true }),
    ironDark: metal('#2b2e34', { roughness: 0.6, flatShading: true }),
    rivet: metal('#8c9099', { roughness: 0.4, flatShading: true }),
    grip: std('#2e2420', { roughness: 0.8, flatShading: true }),
    rope: std('#8f8b84', { roughness: 0.95, flatShading: true }),
    ore: stone('#47464c'),
    banner: std('#24272e', { roughness: 0.95, flatShading: true }),
    wool: std('#c0392b', { roughness: 0.95, flatShading: true }),
    slab: stone('#6a6e76'),
    slabDark: stone('#2a2b30'),
    // The tokens.
    slate: stone('#6b7480'),
    slateDark: stone('#4a515b'),
    basalt: stone('#2b2a2e'),
    obsidian: std('#0e0c12', { roughness: 0.15, metalness: 0.3, flatShading: true }),
    teal: stone('#1f4d4c'),
    water: std('#5fb8b0', { roughness: 0.1, flatShading: true }),
    root: std('#5a3f2c', { roughness: 0.95, flatShading: true }),
    rootDark: std('#3c2a1e', { roughness: 0.95, flatShading: true }),
    stem: std('#cfd8d4', { roughness: 0.8, flatShading: true }),
    cap: std('#4fe3ee', { roughness: 0.6, flatShading: true }),
    capDots: std('#d8fbff', { roughness: 0.6, flatShading: true }),
    jar: std('#9fc6dc', { roughness: 0.15, metalness: 0.1, flatShading: true }),
    frost: std('#eef8ff', { roughness: 0.4, flatShading: true }),
    cork: std('#4a3a2e', { roughness: 0.9, flatShading: true }),
    geodeRock: stone('#4a4650'),
    violet: std('#9a62f0', { roughness: 0.3, flatShading: true }),
    parchment: std('#c4c7cc', { roughness: 0.9, flatShading: true }),
    parchDark: std('#8d9096', { roughness: 0.9, flatShading: true }),
    wax: std('#b3261c', { roughness: 0.5, flatShading: true }),
    sootIron: metal('#1a1a1d', { roughness: 0.7, flatShading: true }),
    silk: std('#e4e7ec', { roughness: 0.9, flatShading: true }),
    silkDark: std('#a9aeb7', { roughness: 0.9, flatShading: true }),
    bone: std('#9c9c98', { roughness: 0.85, flatShading: true }),
  };
  const merge = (src: Record<string, THREE.BufferGeometry[]>, parent: THREE.Object3D): void => {
    for (const [k, list] of Object.entries(src)) {
      if (!list.length) continue;
      const merged = mergeGeometries(list.map((x) => (x.index ? x.toNonIndexed() : x)), false);
      for (const x of list) x.dispose();
      if (merged) M(merged, mats[k] as THREE.Material, parent);
    }
  };
  merge(parts, g);
  merge(sp, slab);
  for (const [color, size, p, op] of glows) glowSprite(g, color, size, p, op);
  return done(c, 3.2);
};
